import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';

export type NavigationItem = {
  type: string;
  activeIcon: string;
  icon: string;
  langKey: string;
  langKeyMobile?: string;
  notificationKey?: string;
  hidden?: boolean;
  authorized?: boolean;
  function: 'link' | 'logout' | 'stopImpersonate';
  desktopOnly?: boolean;
  isSalesToolPage?: boolean;
};

export default function useMyPageNavigation() {
  const userStore = useUserStore();
  const globalContent = useGlobalContentStore();
  const itemList = ref<NavigationItem[]>([]);
  const secondItemList = ref<NavigationItem[]>([]);

  const setNavigationList = (() => {
    itemList.value = [];
    secondItemList.value = [];

    // * * * * * * * * * * * * mediabank user - no access to my pages at all, only logout
    if (userStore.isMediabankUser) {
      itemList.value.push({ type: 'logout', activeIcon: '/icons/log-out.svg', icon: '/icons/log-out.svg', langKey: 'mypage.logout', function: 'logout' });
      return;
    }

    // * * * * * * * * * * * * samplesOnly B2B user - only sample pages and settings
    if (userStore.isSamplesOnlyUser) {
      if (globalContent.getSampleRequestNavEnabled) {
        itemList.value.push({ type: 'sampleRequests', activeIcon: '/icons/box-open.svg', icon: '/icons/box-open.svg', langKey: 'mypage.sampleRequest', function: 'link' });
      }
      if (globalContent.getSampleInventoryEnabled) {
        itemList.value.push({ type: 'sampleInventory', activeIcon: '/icons/box-open.svg', icon: '/icons/box-open.svg', langKey: 'mypage.sampleInventory', function: 'link' });
      }
      itemList.value.push({ type: 'settings', activeIcon: '/icons/settings.svg', icon: '/icons/settings.svg', langKey: 'mypage.settings', function: 'link' });
      itemList.value.push({ type: 'logout', activeIcon: '/icons/log-out.svg', icon: '/icons/log-out.svg', langKey: 'mypage.logout', function: 'logout' });
      return;
    }

    // * * * * * * * * * * * *  standard customer or customer support impersonating user
    if ((!userStore.isCustomerSuccessUser || userStore.userProfile.impersonatingCompany) && !userStore.isSalesRepUser && !userStore.isCountryManagerUser) {

      itemList.value.push({ type: 'overview', activeIcon: '/icons/layer.svg', icon: '/icons/layer.svg', langKey: 'mypage.overview', authorized: true, function: 'link' });

      itemList.value.push({ type: 'orderHistory', activeIcon: '/icons/history.svg', icon: '/icons/history.svg', langKey: 'mypage.orderHistory', langKeyMobile: 'mypage.orderHistory.mobile', authorized: true, function: 'link' });

      if (globalContent.getAllowBackorders) {
        itemList.value.push({ type: 'backorders', activeIcon: '/mypage/delivery-solid.svg', icon: '/mypage/delivery.svg', langKey: 'mypage.backorders', langKeyMobile: 'mypage.backorders.mobile', function: 'link' });
      }

      itemList.value.push({ type: 'orderProposals', activeIcon: '/icons/order-proposal.svg', icon: '/icons/order-proposal.svg', langKey: 'mypage.orderProposal', langKeyMobile: 'mypage.orderProposal.mobile', notificationKey: 'orderProposal', function: 'link' });

      if (globalContent.getSampleRequestNavEnabled) {
        itemList.value.push({ type: 'sampleRequests', activeIcon: '/icons/box-open.svg', icon: '/icons/box-open.svg', langKey: 'mypage.sampleRequest', function: 'link' });
      }

      if (!globalContent.getDisableInvoices) {
        itemList.value.push({ type: 'invoices', activeIcon: '/icons/invoice.svg', icon: '/icons/invoice.svg', langKey: 'mypage.invoices', notificationKey: 'invoice', function: 'link' });
      }

      if (!userStore.isCustomerSuccessUser) {
        itemList.value.push({ type: 'favorites', activeIcon: '/icons/heart.svg', icon: '/icons/heart.svg', langKey: 'mypage.favorites', function: 'link' });
      }
      itemList.value.push({ type: 'carts', activeIcon: '/icons/cart.svg', icon: '/icons/cart.svg', langKey: 'mypage.carts', function: 'link' });

      if (globalContent.getCountryCode !== 'de') {
        itemList.value.push({ type: 'claims', activeIcon: '/icons/file-send.svg', icon: '/icons/file-send.svg', langKey: 'mypage.claims', function: 'link' });
      }

      if (globalContent.getSampleInventoryEnabled) {
        itemList.value.push({ type: 'sampleInventory', activeIcon: '/icons/box-open.svg', icon: '/icons/box-open.svg', langKey: 'mypage.sampleInventory', function: 'link' });
      }

      itemList.value.push({ type: 'settings', activeIcon: '/icons/settings.svg', icon: '/icons/settings.svg', langKey: 'mypage.settings', function: 'link' });

      if (userStore.isCustomerSuccessUser) {
        itemList.value.push({ type: 'stopImpersonate', activeIcon: '/icons/icon-x-circle.svg', icon: '/icons/icon-x-circle.svg', langKey: 'impersonate.stop', function: 'stopImpersonate' });
      }
      if (!userStore.isCustomerSuccessUser) {
        itemList.value.push({ type: 'onboarding', activeIcon: '/icons/info.svg', icon: '/icons/info.svg', langKey: 'mypage.user.onboarding', function: 'link', desktopOnly: true });

        itemList.value.push({ type: 'logout', activeIcon: '/icons/log-out.svg', icon: '/icons/log-out.svg', langKey: 'mypage.logout', function: 'logout' });
      }
    }

    // * * Second navigation, for customer support
    if (userStore.isCustomerSuccessUser) {
      secondItemList.value.push({ type: 'selectCustomer', activeIcon: '/icons/customer.svg', icon: '/icons/customer.svg', langKey: 'siteNotice.changeSalon.customerSuccess', function: 'link' });
      if (userStore.userProfile.impersonatingCompany) {
        secondItemList.value.push({ type: 'orderProposalsCS', activeIcon: '/icons/order-proposal.svg', icon: '/icons/order-proposal.svg', langKey: 'mypage.orderProposal', function: 'link' });
        secondItemList.value.push({ type: 'fastOrder', activeIcon: '/icons/fast-order.svg', icon: '/icons/fast-order.svg', langKey: 'mypage.sales.fastOrder', function: 'link' });
      }
      secondItemList.value.push({ type: 'logout', activeIcon: '/icons/sign-out.svg', icon: '/icons/sign-out.svg', langKey: 'mypage.logout', function: 'logout' });
    }

    // * * * * * * * * * * * * Sales Rep (also fires for a country manager -
    // isSalesRepUser intentionally includes them, since a country manager
    // gets the full sales-rep toolset plus their extra approval permissions,
    // not a scoped-down version of it)
    if (userStore.isSalesRepUser) {
      if (globalContent.getFastOrderEnabled) {
        itemList.value.push({ type: 'fastOrder', activeIcon: '/icons/fast-order.svg', icon: '/icons/fast-order.svg', langKey: 'mypage.sales.fastOrder', function: 'link', isSalesToolPage: true });
      }

      if (globalContent.getAllowBackorders) {
        itemList.value.push({ type: 'backorders', activeIcon: '/icons/delivery-solid.svg', icon: '/icons/delivery.svg', langKey: 'mypage.backorders.mobile', function: 'link', isSalesToolPage: true });
      }
      itemList.value.push({ type: 'orderProposals', activeIcon: '/icons/order-proposal.svg', icon: '/icons/order-proposal.svg', langKey: 'mypage.sales.orderProposal', function: 'link', isSalesToolPage: true });
      if (globalContent.getSampleRequestNavEnabled) {
        itemList.value.push({ type: 'sampleRequests', activeIcon: '/icons/box-open.svg', icon: '/icons/box-open.svg', langKey: 'mypage.sales.sampleRequest', function: 'link', isSalesToolPage: true });
      }
      itemList.value.push({ type: 'customers', activeIcon: '/icons/customer.svg', icon: '/icons/customer.svg', langKey: 'mypage.sales.customers', function: 'link', isSalesToolPage: true });
      itemList.value.push({ type: 'carts', activeIcon: '/icons/cart.svg', icon: '/icons/cart.svg', langKey: 'mypage.carts', function: 'link', isSalesToolPage: true });

      if (globalContent.getSampleInventoryEnabled) {
        itemList.value.push({ type: 'sampleInventory', activeIcon: '/icons/box-open.svg', icon: '/icons/box-open.svg', langKey: 'mypage.sampleInventory', function: 'link', isSalesToolPage: true });
      }

      if (userStore.userProfile.impersonatingCompany) {
        secondItemList.value.push({ type: 'favorites', activeIcon: '/icons/heart.svg', icon: '/icons/heart.svg', langKey: 'mypage.sales.favorites', function: 'link' });
        secondItemList.value.push({ type: 'orderHistory', activeIcon: '/icons/history.svg', icon: '/icons/history.svg', langKey: 'mypage.sales.orderHistory', function: 'link', isSalesToolPage: true });
        if (!globalContent.getDisableInvoices) {
          secondItemList.value.push({ type: 'invoices', activeIcon: '/icons/invoice.svg', icon: '/icons/invoice.svg', langKey: 'mypage.invoices', notificationKey: 'invoice', function: 'link' });
        }
      }

      secondItemList.value.push({ type: 'settings', activeIcon: '/icons/cog.svg', icon: '/icons/cog.svg', langKey: 'mypage.settings', function: 'link' });
      secondItemList.value.push({ type: 'onboarding', hidden: true, activeIcon: '/icons/info.svg', icon: '/icons/info.svg', langKey: 'mypage.sales.onboarding', function: 'link' });
      secondItemList.value.push({ type: 'logout', activeIcon: '/icons/sign-out.svg', icon: '/icons/sign-out.svg', langKey: 'mypage.logout', function: 'logout' });
    }

  });

  return {
    setNavigationList,
    itemList,
    secondItemList,
  };

}
